import {
  Autocomplete,
  type AutocompleteProps,
  type AutocompleteValue,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { useId, useRef, useState } from 'react';

import {
  type RequireLabelWithOptionalHelpTooltipOrAria,
  getLabelOrAriaProps,
  isDefined,
  isTruthy,
} from '../../utils/index.js';
import { FormField } from '../Form/FormField';
import { SpanWithEllipsisAndTooltipOnOverflow } from '../SpanWithEllipsisAndTooltipOnOverflow.js';
import { HelpTooltip } from '../Tooltips/Help.js';
import { Text } from '../Typography';

export interface ApiAutocompleteOption {
  id: string;
  name: string; // Most common property for display returned by APIs
  helperText?: string;
}

type BaseApiAutocompleteProps<
  TValue extends ApiAutocompleteOption,
  TMultiple extends boolean | undefined,
> = Omit<
  AutocompleteProps<TValue, TMultiple, boolean, false>,
  | 'id'
  | 'label'
  | 'aria-label'
  | 'value'
  | 'onChange'
  | 'filterOptions'
  | 'isOptionEqualToValue'
  | 'filterSelectedOptions'
  | 'inputValue'
  | 'onInputChange'
  | 'renderInput'
  | 'getOptionLabel'
> & {
  inputFieldName?: string;
  id?: string;
  placeholder?: string;
  value: AutocompleteValue<TValue, TMultiple, boolean, false>;
  setValue: (option: AutocompleteValue<TValue, TMultiple, boolean, false> | null) => void;
  inputValue: string;
  setInputValue: (value: string) => void;
  errorMessage?: string;
  helperText?: string;
  variant?: 'outlined' | 'borderless';
} & RequireLabelWithOptionalHelpTooltipOrAria;

export type ApiAutocompleteProps<
  TValue extends ApiAutocompleteOption,
  TMultiple extends boolean | undefined,
> = BaseApiAutocompleteProps<TValue, TMultiple>;

export function ApiAutocomplete<
  TValue extends ApiAutocompleteOption,
  TMultiple extends boolean | undefined = false,
>(props: ApiAutocompleteProps<TValue, TMultiple>) {
  const {
    inputFieldName,
    id,
    label,
    'aria-label': ariaLabel,
    placeholder = 'Type to search...',
    value,
    setValue,
    inputValue,
    setInputValue,
    errorMessage,
    helperText,
    helpTooltipProps,
    variant = 'outlined',
    ...muiProps
  } = props;

  const generatedFieldId = useId();
  const fieldId = id ?? generatedFieldId;
  const isError = isDefined(errorMessage);

  const inputRef = useRef<HTMLInputElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  return (
    <FormField
      id={fieldId}
      {...getLabelOrAriaProps({ label, 'aria-label': ariaLabel })}
      error={errorMessage}
      helperText={helperText}
      labelAdornment={
        isDefined(helpTooltipProps) ? (
          <HelpTooltip {...helpTooltipProps} tooltipProps={{ placement: 'top' }} />
        ) : undefined
      }
    >
      <Autocomplete
        {...muiProps}
        id={fieldId}
        aria-label={ariaLabel}
        value={value}
        onChange={(_, newValue) => {
          setValue(newValue);
          setInputValue('');
        }}
        filterOptions={(options) => options}
        isOptionEqualToValue={(option, value) => {
          return option.id === value.id;
        }}
        filterSelectedOptions
        getOptionLabel={(option) => {
          if (Array.isArray(option)) {
            return option.map((item: TValue) => item.name).join(', ');
          }
          return option.name;
        }}
        inputValue={inputValue ?? ''} // To avoid uncontrolled input warning
        onInputChange={(_, newInputValue, reason) => {
          if (!muiProps.multiple) {
            setInputValue(newInputValue);
          } else if (reason === 'input') {
            setInputValue(newInputValue);
          }
        }}
        renderOption={
          isDefined(muiProps.renderOption)
            ? muiProps.renderOption
            : (props, option, state, ownerState) => (
                /**
                 * `renderOption` is required to ensure that the filtered items have unique keys.
                 * See this article: https://lockstep.io/blog/debugging-lesson-well-learned-fixing-material-ui-autocomplete-search/
                 */
                <MenuItem {...props} key={option.id}>
                  <Stack direction="column" gap={0.5} component="span" sx={{ width: '100%' }}>
                    <Text variant="body2" noWrap>
                      <SpanWithEllipsisAndTooltipOnOverflow tooltipMaxWidth={200}>
                        {ownerState.getOptionLabel(option)}
                      </SpanWithEllipsisAndTooltipOnOverflow>
                    </Text>
                    {isTruthy(option.helperText) && (
                      <Text variant="subtitle2" noWrap>
                        <SpanWithEllipsisAndTooltipOnOverflow tooltipMaxWidth={200}>
                          {option.helperText}
                        </SpanWithEllipsisAndTooltipOnOverflow>
                      </Text>
                    )}
                  </Stack>
                </MenuItem>
              )
        }
        renderInput={(params) => (
          <Tooltip
            title={isOverflowing && !Array.isArray(value) ? value?.name : undefined}
            disableHoverListener={!isOverflowing}
          >
            <TextField
              {...params}
              name={inputFieldName}
              inputRef={inputRef}
              onMouseEnter={() => {
                const input = inputRef.current;
                if (input) {
                  setIsOverflowing(input.scrollWidth > input.clientWidth);
                }
              }}
              size="small"
              variant={variant === 'borderless' ? 'standard' : 'outlined'}
              placeholder={
                muiProps.multiple && Array.isArray(value) && value?.length > 0
                  ? undefined
                  : placeholder
              }
              error={isError}
              slotProps={{
                input: {
                  ...params.InputProps,
                  ...(variant === 'borderless' ? { disableUnderline: true } : {}),
                  endAdornment: (
                    <>
                      {muiProps.loading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          </Tooltip>
        )}
      />
    </FormField>
  );
}
